import React, { useMemo, useState } from "react";
import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Plus, Box, MapPin, Eye, Pencil, Trash2, Search } from "lucide-react";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Card, CardContent } from "@shared/ui/card";
import { WarehouseForm } from "../create/create";
import { useWarehouses } from "../../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PAGE_SIZE } from "@shared/utils/constants";

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  description: string;
  address: string;
  coordinates: string;
  created: string;
  createdBy: string;
}
// Warehouse Detail Modal Component
function WarehouseDetailDialog({
  isOpen,
  onClose,
  data,
}: {
  isOpen: boolean;
  onClose: () => void;
  data?: any;
}) {
  if (!data) return null;

  const createdDate = data.createdDate || "NA";
  const createdBy = data.createdUserRemoteName || "NA";
  const coordinates = (data.latitude && data.longitude) ? `${data.latitude}, ${data.longitude}` : (data.coordinates || "NA");

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
        <DialogHeader className="border-b pb-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Warehouse detail
              </DialogTitle>
              <p className="text-xs text-gray-500 mt-0.5">Code: {data.code || "NA"}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-4 overflow-y-auto max-h-[420px] pr-2">
          <Card className="border-gray-200 shadow-none bg-gray-50/30">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ValueLabel label="Name" value={data.name || "NA"} />
              <ValueLabel label="Code" value={data.code || "NA"} />
              <ValueLabel label="Address" value={data.address || "NA"} />
              <ValueLabel label="Coordinates" value={coordinates} />
              <ValueLabel label="Created date" value={createdDate} />
              <ValueLabel label="Created by" value={createdBy} />
              <div className="md:col-span-2">
                <ValueLabel label="Description" value={data.description || "No description provided."} />
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WarehouseList() {
  const remoteUtilityId = useRemoteUtilityId();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { data: warehousesResponse, isLoading } = useWarehouses(remoteUtilityId, search, currentPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState<Warehouse | undefined>(undefined);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<Warehouse | undefined>(undefined);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [viewData, setViewData] = useState<Warehouse | undefined>(undefined);

  const data = warehousesResponse?.results || [];

  const columns: TableColumn<Warehouse>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        type: "text",
        render: (value) => <span className="font-semibold text-gray-900">{value}</span>,
      },
      {
        key: "code",
        header: "Code",
        type: "custom",
        render: (value) => (
          <Badge variant="outline" className="text-gray-600 bg-gray-50 border-gray-200">
            {value}
          </Badge>
        ),
      },
      {
        key: "description",
        header: "Description",
        render: (value) => <HoverRevealText text={value || "—"} />,

      },
      {
        key: "address",
        header: "Address",
        type: "text",
        render: (value) => <span className="text-gray-600 font-medium">{value}</span>,
      },
      {
        key: "coordinates",
        header: "Coordinates",
        type: "custom",
        render: (_, row: any) => {
          const lat = row.latitude;
          const lon = row.longitude;
          if (lat && lon) {
            return <span className="text-gray-500 font-medium">{`${lat}, ${lon}`}</span>;
          }
          return <span className="text-gray-500 font-medium">{row.coordinates || "NA"}</span>;
        },
      },
      {
        key: "createdUserRemoteName",
        header: "Created",
        type: "custom",
        render: (value, row: any) => {
          const dateStr = row.createdDate;
          const userStr = row.createdUserRemoteName || "System";
          return (
            <div className="flex flex-col">
              <span className="font-medium text-gray-950">{dateStr.replace(/-/g, "/")}</span>
              <p className="text-[11px] text-gray-500 mt-0.5">{userStr}</p>
            </div>
          );
        },
      },
    ],
    []
  );

  const handleView = (row: Warehouse) => {
    setViewData(row);
    setIsDetailOpen(true);
  };

  const handleEdit = (row: Warehouse) => {
    setEditData(row);
    setIsFormOpen(true);
  };

  const handleDelete = (row: Warehouse) => {
    setDeleteItem(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    console.log("Deleted warehouse successfully:", deleteItem?.id);
    setIsDeleteOpen(false);
    setDeleteItem(undefined);
  };

  const handleSave = (savedData: any) => {
    console.log("Saved warehouse data:", savedData);
    setIsFormOpen(false);
  };

  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Edit",
      onClick: handleEdit,
      icon: "Pencil",
      variant: "outline",
    },
    {
      label: "Delete",
      onClick: handleDelete,
      icon: "Trash2",
      variant: "outline",
      disabled: () => true
    },
  ];

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: warehousesResponse?.total_pages || Math.ceil((warehousesResponse?.count || 0) / PAGE_SIZE) || 1,
      totalItems: warehousesResponse?.count || 0,
      itemsPerPage: PAGE_SIZE,
      startItem: warehousesResponse?.count ? (currentPage - 1) * PAGE_SIZE + 1 : 0,
      endItem: Math.min(currentPage * PAGE_SIZE, warehousesResponse?.count || 0),
    }),
    [currentPage, warehousesResponse]
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Warehouses"
        subtitle="Manage and view all your warehouses in one place."
      >
        <Button onClick={() => { setEditData(undefined); setIsFormOpen(true); }} className="flex items-center gap-1">
          <Plus className="w-4.5 h-4.5" />
          <span>Add warehouse</span>
        </Button>
      </PageHeader>

      <GenericTable
        data={data}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        onSearch={(val) => {
          setSearch(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search warehouses..."
      />

      <WarehouseForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        editData={editData}
        onSuccess={handleSave}
      />

      <WarehouseDetailDialog
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setViewData(undefined);
        }}
        data={viewData}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteItem(undefined);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete warehouse"
        itemName={deleteItem?.name}
      />
    </div>
  );
}
